<template>
  <HeaderBar />
  <div class="login-page">
    <el-card class="login-card" shadow="hover" role="region" aria-label="Login form">
      <el-row :gutter="16">
        <el-col :xs="24" :md="13" class="card-left">
          <h1 class="card-title">Welcome</h1>
          <p class="card-subtitle">Sign to continue</p>
          <form @submit.prevent.stop="submitForm" class="login-form" autocomplete="on">
            <el-form label-position="top">
              <el-form-item>
                <el-input id="email" v-model="email" type="email" placeholder="Email" clearable style="width: 350px; display: flex; margin: 0 auto;">
                  <template #prefix>
                    <el-icon size="large"><Message /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-form-item>
                <el-input
                  id="password"
                  v-model="password"
                  type="password"
                  placeholder="Password"
                  show-password
                  style="width: 350px; display: flex; margin: 0 auto;"
                >
                <template #prefix>
                    <el-icon size="large"><Lock /></el-icon>
                  </template>
                </el-input>
              </el-form-item>
              <el-button
                type="primary"
                native-type="button"
                size="large"
                :loading="submitting"
                @click.prevent="submitForm"
                style=" width:350px; display: flex; margin: 0 auto;"
              >
                Login
              </el-button>
            </el-form>

            <el-divider content-position="center" style="width: 350px; display: flex; margin: 15px auto;">Or</el-divider>
            
            <el-button
              type="default"
              native-type="button"
              class="google-btn"
              @click.prevent="signInWithGoogle"
              aria-label="Sign in with Google"
              style="display: flex; margin: 0 auto;"
            >
              <img src="@/assets/google-signin-logo.png" alt="Sign in with Google" />
            </el-button>
          </form>
          <p class="signup-text" style="display: flex; margin: 10px auto;">
            Don't have an account?
            <router-link to="/signup" class="signup-link">Sign Up</router-link>
          </p>
        </el-col>
        <el-col :xs="24" :md="11" class="card-right" aria-hidden="false">
          <div class="hero-slot">
            <img src="@/assets/Login-Page-Animation.jpg" alt="Illustration" />
          </div>
          <div class="company-info">
            <h3>Bridging Gaps in Research</h3>
            <p>We connect researchers and resources to fuel discovery.</p>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider, signOut, deleteUser } from 'firebase/auth'
import HeaderBar from '@/components/HeaderBar.vue'
import { ElMessage } from 'element-plus'
import { getUserRoleFromProfile, resolveUserProfile } from '@/services/userProfile'

export default {
  name: 'LoginView',
  components: { HeaderBar },
  data() {
    return { email: '', password: '', error: '', success: '', submitting: false, passwordVisible: false }
  },
  methods: {
    getPostLoginRoute(profile) {
      return getUserRoleFromProfile(profile) === 'Owner' ? 'Dashboard' : 'CompleteProfile'
    },
    showMessage(type, message) {
      if (!message) return
      ElMessage({ type, message, showClose: true, duration: 4500 })
    },
    togglePasswordVisibility() { this.passwordVisible = !this.passwordVisible },
    formatAuthError(code) {
      const map = {
        'auth/invalid-email': 'Invalid email address.',
        'auth/user-disabled': 'This account is disabled.',
        'auth/user-not-found': 'No account found for this email.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-credential': 'Invalid credentials. Please try again.',
        'auth/invalid-login-credentials': 'Incorrect password. Please try again.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.'
      }
      return map[code] || null
    },
    async submitForm() {
      if (this.submitting) return
      this.error = ''
      this.success = ''
      const emailInput = (this.email || '').trim().toLowerCase()
      const password = this.password || ''
      if (!emailInput || !password) {
        this.error = 'Please enter both email and password.'
        this.showMessage('error', this.error)
        return
      }
      this.submitting = true
      try {
        const cred = await signInWithEmailAndPassword(auth, emailInput, password)
        const user = cred?.user || auth.currentUser
        const profile = await resolveUserProfile(user).catch(() => null)
        if (!profile?.exists) {
          await signOut(auth).catch(() => {})
          this.error = 'No account exists for this email.'
          this.showMessage('error', this.error)
          return
        }
        this.email = ''
        this.password = ''
        this.success = 'Login successful.'
        this.showMessage('success', this.success)
        this.$router.push({ name: this.getPostLoginRoute(profile) })
      } catch (err) {
        const friendly = this.formatAuthError(err?.code)
        this.error = friendly || err?.message || 'Login failed. Please check your credentials.'
        this.showMessage('error', this.error)
      } finally {
        this.submitting = false
      }
    },
    async signInWithGoogle() {
      if (this.submitting) return
      this.error = ''
      this.success = ''
      this.submitting = true
      try {
        const provider = new GoogleAuthProvider()
        const cred = await signInWithPopup(auth, provider)
        const email = (cred.user?.email || '').toLowerCase()
        if (!email) throw new Error('Google account has no email')
        const profile = await resolveUserProfile(cred.user).catch(() => null)
        if (!profile?.exists) {
          const userToDelete = auth.currentUser || cred.user
          if (userToDelete) {
            await deleteUser(userToDelete).catch(() => {})
          }
          await signOut(auth).catch(() => {})
          this.error = 'No account exists for this email.'
          this.showMessage('error', this.error)
          return
        }
        this.success = 'Login successful. Redirecting...'
        this.showMessage('success', this.success)
        this.$router.push({ name: this.getPostLoginRoute(profile) })
      } catch (err) {
        const friendly = this.formatAuthError(err?.code)
        this.error = friendly || err?.message || 'Google sign-in failed'
        this.showMessage('error', this.error)
      } finally {
        this.submitting = false
      }
    },
  }
}
</script>

<style scoped>
.login-page {
  font-family: "Roboto", "Segoe UI", sans-serif, "Helvetica Neue", Arial, system-ui, -apple-system;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(180deg, #f3f8ff 0%, #e8f0ff 100%);
  padding: 24px 1rem 40px;
  box-sizing: border-box;
  overflow: hidden;
}

.login-card {
  width: min(900px, 96%);
  border-radius: 22px;
  box-shadow: 0 16px 40px rgba(12,34,60,0.12);
  background: #ffffff;
  color: #003831;
}

.card-left {
  padding: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  box-sizing: border-box;
  background: #ffffff;
  font-size: 1.05rem;
}

.card-title {
  align-self: center;
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #003831;
}

.card-subtitle {
  align-self: center;
  margin: 0 0 12px 0;
  color: #476e67;
  font-size: 16px;
}

.login-form { display:flex; flex-direction:column; gap:12px; margin-top:8px; }

.google-btn {
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: inline-block;
}

.google-btn img {
  display: flex;
  height: 42px;
  width: auto;
  max-width: 100%;
  border: 1px solid rgba(3,56,49,0.12);
  border-color: grey;
  box-shadow: 0 6px 12px rgba(3,56,49,0.06);
}


.google-btn:focus {
  outline: 3px solid rgba(11,92,255,0.16);
  border-radius: 6px;
}

.signup-text {
  margin-top:10px;
  color:#516a64;
  font-size:0.95rem;
}

.signup-link {
  color: #0b6cff;
  text-decoration: none;
  font-weight: 700;
  margin-left:6px;
}
.signup-link:hover { text-decoration: underline; }

.card-right {
  width: 48%;
  padding: 20px;
  background: linear-gradient(180deg, #003831 0%, #1a6a54 100%);
  color: #ffffff;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:18px;
  box-sizing:border-box;
}

.hero-slot img {
  max-width: 160px;
  width: 100%;
  height: auto;
  display:block;
  border-radius:12px;
  box-shadow: 0 12px 30px rgba(14,50,130,0.16);
  background: rgba(255,255,255,0.08);
}

.company-info { text-align:center; max-width:220px; }
.company-info h3 { margin:0; font-size:1.05rem; font-weight:700; color:#fff; }
.company-info p { margin:6px 0 0 0; font-size:0.95rem; color:rgba(255,255,255,0.9); }

@media (max-width: 880px) {
  .login-card { width: 100%; margin-top: 160px; }
  .card-left, .card-right { width:100%; padding:24px; }
  .card-right {
    order: -1;
    padding-bottom:8px;
    border-radius:0 0 22px 22px;
  }
  .company-logo { height:48px; }
  .hero-slot img {
    max-width: 140px;
  }
}
</style>
